import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SketchWhiteboard.css';

/**
 * A whiteboard for one sketch answer in a live interview.
 *
 * Purpose-built rather than adapted from the notes feature's
 * CanvasDrawingWorkspace: that component imports notesAPI at module scope and
 * carries highlighter, lasso, text, image-insert, multi-page and templates.
 * The technique here is the same as its (pointer events building a stroke
 * array, replayed onto a canvas); none of the coupling is.
 *
 * Self-contained on purpose. It takes the rung's sketch brief in and hands a
 * PNG data URL out; it does not know about runs, endpoints or scoring, so it
 * can be dropped into whichever screen ends up hosting the conversation.
 *
 * Props:
 *   what      the brief — sketch.what from the rung, shown above the canvas
 *   onSubmit  (dataUrl) => void, called with a PNG capped at MAX_EDGE
 *   busy      disables submit while a caller is scoring
 *   disabled  read-only
 *   scratch   unscored working space: no submit, and says so
 */

// Matches sketch_check.MAX_EDGE. An image is billed at about (w x h) / 750
// tokens, so this is what keeps a call near 930 input tokens. The backend
// caps it again — a client is a client — but sending it already capped means
// the candidate's own drawing is what gets scored, not a resampling of it.
const MAX_EDGE = 1000;

const PEN_WIDTH = 3;
const ERASER_WIDTH = 24;
const INK = '#111827';
const PAPER = '#ffffff';

const TOOLS = [
  { id: 'pencil', label: 'Draw' },
  { id: 'line', label: 'Line' },
  { id: 'eraser', label: 'Erase' },
];

function strokeStyleFor(tool) {
  if (tool === 'eraser') return { color: PAPER, width: ERASER_WIDTH };
  return { color: INK, width: PEN_WIDTH };
}

/** Replay every stroke onto a context already scaled to `scale`. Shared by
 * the on-screen canvas and the export, so what is submitted is what was
 * drawn rather than a second, slightly different rendering of it. */
function paint(ctx, strokes, width, height, scale) {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, width * scale, height * scale);
  ctx.scale(scale, scale);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  strokes.forEach((stroke) => {
    if (!stroke.points.length) return;
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.beginPath();
    ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
    if (stroke.points.length === 1) {
      // A tap still leaves a dot, which is what a pen does.
      ctx.lineTo(stroke.points[0].x + 0.01, stroke.points[0].y);
    } else {
      stroke.points.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
    }
    ctx.stroke();
  });
  ctx.restore();
}

export default function SketchWhiteboard({
  what, onSubmit, busy = false, disabled = false, scratch = false,
}) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawingRef = useRef(false);
  const [strokes, setStrokes] = useState([]);
  const [tool, setTool] = useState('pencil');
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [warning, setWarning] = useState('');

  // The canvas is sized from its container and backed at devicePixelRatio, or
  // lines look soft on any retina screen.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    // clientWidth, not getBoundingClientRect(): the wrap has a 1px border, and
    // a canvas as wide as the border box is 2px wider than the room inside it.
    // In the desktop grid that pushed the column 2px wider, which the observer
    // saw as a resize, which widened the canvas again: the whiteboard grew
    // across the whole screen, about 120px a second.
    const measure = () => {
      const width = Math.max(1, Math.floor(wrap.clientWidth));
      const height = Math.max(1, Math.round(width * 0.66));
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !size.width) return;
    const scale = window.devicePixelRatio || 1;
    canvas.width = size.width * scale;
    canvas.height = size.height * scale;
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;
    paint(canvas.getContext('2d'), strokes, size.width, size.height, scale);
  }, [strokes, size]);

  const pointAt = useCallback((event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }, []);

  const onPointerDown = useCallback((event) => {
    if (disabled) return;
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    setWarning('');
    const point = pointAt(event);
    const { color, width } = strokeStyleFor(tool);
    // A line keeps two points and moves the second; a freehand stroke grows.
    setStrokes((prev) => [
      ...prev,
      { points: tool === 'line' ? [point, point] : [point], tool, color, width },
    ]);
  }, [disabled, pointAt, tool]);

  const onPointerMove = useCallback((event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const point = pointAt(event);
    setStrokes((prev) => {
      if (!prev.length) return prev;
      const rest = prev.slice(0, -1);
      const current = prev[prev.length - 1];
      const points = current.tool === 'line'
        ? [current.points[0], point]
        : [...current.points, point];
      return [...rest, { ...current, points }];
    });
  }, [pointAt]);

  const endStroke = useCallback((event) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas && event && canvas.hasPointerCapture?.(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId);
    }
  }, []);

  const undo = useCallback(() => {
    setWarning('');
    setStrokes((prev) => prev.slice(0, -1));
  }, []);

  const clear = useCallback(() => {
    setWarning('');
    setStrokes([]);
  }, []);

  /** Render at the capped size onto an offscreen canvas with a real white
   * background. A canvas exports transparent by default, and transparent
   * pixels carry RGB (0,0,0) — so an unflattened export reads as black. */
  const exportPng = useCallback(() => {
    const scale = Math.min(1, MAX_EDGE / Math.max(size.width, size.height));
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(size.width * scale));
    out.height = Math.max(1, Math.round(size.height * scale));
    paint(out.getContext('2d'), strokes, size.width, size.height, scale);
    return out.toDataURL('image/png');
  }, [strokes, size]);

  const hasInk = strokes.some((s) => s.tool !== 'eraser' && s.points.length);

  const submit = useCallback(() => {
    // The backend refuses a blank canvas too, but the candidate should hear
    // it from the button rather than from a round trip.
    if (!hasInk) {
      setWarning('There is nothing on the whiteboard yet.');
      return;
    }
    onSubmit(exportPng());
  }, [hasInk, exportPng, onSubmit]);

  return (
    <div className="sketch-whiteboard">
      {what ? (
        <p className="sketch-whiteboard__brief">
          <span className="sketch-whiteboard__brief-label">Draw</span> {what}
        </p>
      ) : null}

      <div className="sketch-whiteboard__toolbar" role="toolbar" aria-label="Drawing tools">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`sketch-whiteboard__tool${tool === t.id ? ' is-active' : ''}`}
            aria-pressed={tool === t.id}
            disabled={disabled}
            onClick={() => setTool(t.id)}
          >
            {t.label}
          </button>
        ))}
        <span className="sketch-whiteboard__spacer" />
        <button
          type="button"
          className="sketch-whiteboard__tool"
          onClick={undo}
          disabled={disabled || !strokes.length}
        >
          Undo
        </button>
        <button
          type="button"
          className="sketch-whiteboard__tool"
          onClick={clear}
          disabled={disabled || !strokes.length}
        >
          Clear
        </button>
      </div>

      <div className="sketch-whiteboard__canvas-wrap" ref={wrapRef}>
        <canvas
          ref={canvasRef}
          className="sketch-whiteboard__canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
        />
      </div>

      {scratch ? (
        // Nothing drawn here is sent anywhere, so the candidate must not
        // think the examiner can see it.
        <p className="sketch-whiteboard__scratch-note">
          Scratch space for your own working. The examiner goes on what you say
          or type, so talk them through anything you draw here.
        </p>
      ) : (
        <div className="sketch-whiteboard__footer">
          <span className="sketch-whiteboard__warning" role="status">{warning}</span>
          <button
            type="button"
            className="sketch-whiteboard__submit"
            onClick={submit}
            disabled={disabled || busy}
          >
            {busy ? 'Looking at your drawing…' : 'Submit drawing'}
          </button>
        </div>
      )}
    </div>
  );
}

export { MAX_EDGE, paint };
